import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import _isEqual from 'lodash/isEqual';

import { parseRoute } from '@ott/flights-search-form';
import FormattedPeriod from '@ott/formatted-period';
import FormattedDate from '@ott/formatted-date';
import CloseIcon from '@ott/icon-close';
import Timer from '@ott/timer';
import KM from '@ott/utility-km';
import Button from '@ott/button';

import { l10n, lang } from '@ott/l10n';

import cls from 'classnames/bind';
import {
  getUnconfirmedOrdersFromLocalStorage,
  setUnconfirmedOrderHidden,
} from './helpers/ordersLocalStorage';
import { getOrders, cancelOrders } from './reducers/order/actions';
import classes from './ReservationBanner.scss';

const cx = cls.bind(classes);

const PRODUCTS = {
  AVIA: 'avia',
  SOLAR_AVIA: 'solar-avia',
  RAILWAYS: 'railways',
};

const mapStateToProps = (state) => {
  const {
    common: { unconfirmedOrders = {} },
  } = state;

  const { orders, ordersStatus } = unconfirmedOrders;

  return {
    orders,
    ordersStatus,
  };
};

@connect(mapStateToProps)
class ReservationBanner extends React.PureComponent {
  static propTypes = {
    isAuth: PropTypes.bool,
    isMobile: PropTypes.bool,
    useHash: PropTypes.bool,
    product: PropTypes.string.isRequired,
    dispatch: PropTypes.func.isRequired,
  };

  static defaultProps = {
    isMobile: false,
    useHash: false,
    isAuth: false,
  };

  state = {
    isVisible: false,
    orders: [],
  };

  componentDidMount() {
    const { isAuth, dispatch } = this.props;

    if (isAuth) {
      dispatch(getOrders());
    }
    this.timer = setInterval(() => this.syncWithLocalStorage(), 1000);
  }

  componentDidUpdate(prevProps) {
    const { isAuth, dispatch } = this.props;

    if (!prevProps.isAuth && isAuth) {
      dispatch(getOrders());
    }
  }

  componentWillUnmount() {
    clearInterval(this.timer);
  }

  syncWithLocalStorage() {
    const { id, product } = this.getActiveOrder() || {};

    const orders = getUnconfirmedOrdersFromLocalStorage() || [];

    if (!_isEqual(orders, this.state.orders)) {
      const isVisible = orders.some((order) => order && !order.isRemoved);
      this.setState({ orders, isVisible });

      // заказ удален
      if (id && !orders.find((order) => order.id === id)) {
        KM('left_booking_timeout', {
          obj: {
            timestamp: Date.now(),
            id,
            product,
          },
        });
      }
    }
  }

  getActiveOrder = () => {
    const { orders } = this.state;
    const { product } = this.props;
    const notRemovedOrders = (orders || []).filter((order) => !order.isRemoved);

    let filteredOrders = [];
    // приоритет на заказы авиа, кроме случая жд вайтлебла
    if (product === PRODUCTS.SOLAR_AVIA) {
      filteredOrders = notRemovedOrders.filter((order) => order.product === PRODUCTS.SOLAR_AVIA);
    } else if (product === PRODUCTS.AVIA || !__WHITE_LABEL__) {
      filteredOrders = notRemovedOrders.filter((order) => order.product === PRODUCTS.AVIA);
    } else {
      filteredOrders = notRemovedOrders.filter((order) => order.product === PRODUCTS.RAILWAYS);
    }

    if (!filteredOrders.length) {
      filteredOrders = notRemovedOrders;
    }

    return filteredOrders[filteredOrders.length - 1] || null;
  };

  getUrlForConfirmation = (order) => {
    const { id, product, query } = order;

    switch (product) {
      case PRODUCTS.RAILWAYS:
        const isKz = lang === 'ru-kz';
        const url = `/poezda/pay/${id}?smrk=1,7,left_booking_railways${query ? '&' : ''}${query}`;
        return isKz ? url : `/ru${url}`;
      case PRODUCTS.SOLAR_AVIA:
        return `/${lang}/avia/pay/${id}?smrk=1,7,left_booking`;
      case PRODUCTS.AVIA:
      default:
        return `/${lang}/f/#/pay/${id}?smrk=1,7,left_booking`;
    }
  };

  getRestTimeNode(order) {
    const timeOfEndOrdering = new Date(order.reservationEndTime);

    return (
      <Timer
        className={cx('timer')}
        to={timeOfEndOrdering}
        showTimeIcon={false}
        showEmptyHours={false}
      />
    );
  }

  prepareOrder(order) {
    if (order.product === PRODUCTS.AVIA || order.product === PRODUCTS.SOLAR_AVIA) {
      let { fromCode: from, toCode: to, directions, isMW, ...rest } = parseRoute(order.routeKey);

      if (isMW) {
        directions = directions.map(({ date, fromCode, toCode }) => ({
          date,
          from: fromCode,
          to: toCode,
        }));
      }

      return {
        ...order,
        ...rest,
        isMW,
        from,
        to,
        directions,
      };
    }

    return order;
  }

  closeBanner = (event, shouldRemoveAll = false) => {
    const { id, product } = this.getActiveOrder() || {};

    KM(event, {
      obj: {
        timestamp: Date.now(),
        id,
        product,
      },
    });

    this.setState({
      isVisible: false,
    });

    setUnconfirmedOrderHidden(shouldRemoveAll ? null : id);
  };

  handlePay = (e) => {
    const { useHash } = this.props;
    const order = this.getActiveOrder() || {};

    if (useHash) {
      e.preventDefault();
      const url = this.getUrlForConfirmation(order);
      window.location.hash = url.slice(url.indexOf('#') + 1);
    }

    KM('left_booking_return', {
      obj: {
        timestamp: Date.now(),
        id: order.id,
        product: order.product,
      },
    });
  };

  handleCloseClick = () => {
    const { dispatch } = this.props;
    const { orders } = this.state;

    const activeRailwaysOrders = (orders || []).reduce((acc, order) => {
      if (order.isRemoved || order.product !== PRODUCTS.RAILWAYS) {
        return acc;
      }

      return [...acc, order.id];
    }, []);

    dispatch(cancelOrders(activeRailwaysOrders));

    this.closeBanner('left_booking_quit', true);
  };

  renderDirectionsNode(order) {
    const { isMobile } = this.props;
    if (order.isMW) {
      const { directions } = order;
      return directions.map((dir, index) => {
        const date = new Date(dir.date);
        const showWbr = !isMobile && index + 1 !== directions.length;
        return (
          <React.Fragment>
            <div className={cx('direction')}>
              <span className={cx('cities')}>
                <span className={cx('city')}>{dir.from}</span> —{' '}
                <span className={cx('city')}>{dir.to}</span>
              </span>
              <FormattedDate className={cx('dates')} date={date} isShortMonth={true} />;
            </div>
            {showWbr && <wbr />}
          </React.Fragment>
        );
      });
    }

    return (
      <React.Fragment>
        <span className={cx('cities')}>
          <span className={cx('city')}>{order.from}</span> —{' '}
          <span className={cx('city')}>{order.to}</span>
        </span>
        {this.renderFormattedPeriodNode(order)}
      </React.Fragment>
    );
  }

  renderFormattedPeriodNode(order) {
    const fromDate = new Date(order.fromDate);
    const toDate = new Date(order.toDate);

    if (order.isRT) {
      return (
        <FormattedPeriod
          className={cx('dates')}
          startDate={fromDate}
          endDate={toDate}
          isShortMonth={true}
        />
      );
    }

    return <FormattedDate className={cx('dates')} date={fromDate} isShortMonth={true} />;
  }

  renderRoute(order) {
    const preparedOrder = this.prepareOrder(order);
    return (
      <div className={cx('route-wrapper')}>
        <div className={cx('route', { 'route--isMW': preparedOrder.isMW })}>
          {this.renderDirectionsNode(preparedOrder)}
        </div>
      </div>
    );
  }

  checkIfViewApp() {
    if (typeof window !== 'undefined') {
      return window.location.search.includes('view=app');
    }

    return false;
  }

  render() {
    const { isVisible } = this.state;
    const { isMobile, product } = this.props;

    const order = this.getActiveOrder();

    if (!isVisible || (__WHITE_LABEL__ && order.product !== product) || this.checkIfViewApp()) {
      return null;
    }

    const timerNode = this.getRestTimeNode(order);
    const url = this.getUrlForConfirmation(order);

    return (
      <div className={cx('ReservationBanner', { isMobile })} data-locator="reservation-banner">
        {isMobile && (
          <CloseIcon className={cx('close', 'close--mobile')} onClick={this.handleCloseClick} />
        )}
        <div className={cx('rest-time')}>
          {l10n('reservationBanner.restTime')}&nbsp;{timerNode}
        </div>
        {this.renderRoute(order)}
        <div className={cx('actions')}>
          <Button
            type="button"
            href={url}
            target="_blank"
            className={cx('button')}
            onClick={this.handlePay}
            specificLocators="reservation-banner-button"
          >
            {l10n('reservationBanner.goToPay')}
          </Button>
          {!isMobile && (
            <CloseIcon
              className={cx('close')}
              onClick={this.handleCloseClick}
              data-locator="reservation-banner-close"
            />
          )}
        </div>
      </div>
    );
  }
}

export default ReservationBanner;
